import React, { useState } from 'react';
import { toast } from 'sonner';
import { Copy, Users, UserCheck, DollarSign, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';

// Audit view of a consultant's referrals and the commissions their payments
// generated (backend api/v1/affiliates.py → _affiliate_report). Shared by the
// consultant's own /afiliados page and the admin panel's Consultores tab.

const TIER_LABELS = { trial: 'Prueba', entrepreneur: 'Emprendedor', pro: 'Pro', elite: 'Elite' };

const STATUS_LABELS = {
  active: 'Activa',
  past_due: 'Pago pendiente',
  unpaid: 'Impaga',
  paused: 'Pausada',
  canceled: 'Cancelada',
  incomplete: 'Incompleta',
  none: 'Sin suscripción',
};

export const formatMoney = (cents, currency = 'usd') =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format((cents || 0) / 100);

export const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const formatMonth = (key) => {
  const [year, month] = key.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('es-CO', { year: 'numeric', month: 'long' });
};

export const referralLink = (code) => `${window.location.origin}/?ref=${code}`;

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
  </div>
);

const Section = ({ title, children, empty }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
    {empty ? <p className="text-sm text-muted-foreground py-4 text-center">{empty}</p> : children}
  </div>
);

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 text-xs font-medium text-muted-foreground ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);
const Td = ({ children, right, muted }) => (
  <td className={`px-3 py-2 ${right ? 'text-right tabular-nums' : ''} ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>{children}</td>
);

export default function AffiliateReport({ report }) {
  const [tab, setTab] = useState('referrals');
  const { affiliate, current_term: term, referrals, commissions, monthly_totals: monthly, totals } = report;
  const link = referralLink(affiliate.code);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar el enlace');
    }
  };

  const daysLeft = term ? Math.max(0, Math.ceil((new Date(term.term_end) - new Date()) / 86400000)) : 0;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="text-xs text-muted-foreground mb-2">Tu enlace de afiliado</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <code className="flex-1 min-w-0 truncate rounded-md bg-muted px-3 py-2 text-sm text-foreground">{link}</code>
          <Button onClick={copyLink} className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">
            <Copy className="w-4 h-4 mr-2" /> Copiar enlace
          </Button>
        </div>
        {affiliate.status !== 'active' && (
          <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
            Este enlace está desactivado: los nuevos registros no quedan asociados.
          </p>
        )}
        {term ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Acuerdo vigente: {formatDate(term.term_start)} – {formatDate(term.term_end)} ({daysLeft} días restantes) ·{' '}
            {term.commission_pct}% de comisión · {term.customer_discount_pct}% de descuento por {term.customer_discount_months} meses para tus referidos.
          </p>
        ) : (
          <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
            No tienes un acuerdo vigente: los nuevos registros no obtienen descuento ni generan comisión.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={Users} label="Referidos" value={totals.referrals} />
        <StatTile icon={UserCheck} label="Con suscripción activa" value={totals.active_referrals} />
        <StatTile icon={DollarSign} label="Comisión acumulada" value={formatMoney(totals.commission_cents)} />
        <StatTile icon={CalendarClock} label="Días de acuerdo restantes" value={term ? daysLeft : '—'} />
      </div>

      <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
        {[
          { id: 'referrals', label: 'Referidos' },
          { id: 'monthly', label: 'Resumen mensual' },
          { id: 'payments', label: 'Pagos' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'referrals' && (
        <Section title="Usuarios registrados con tu enlace" empty={referrals.length === 0 && 'Todavía no hay usuarios registrados con tu enlace.'}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr><Th>Usuario</Th><Th>Registro</Th><Th>Plan</Th><Th>Suscripción</Th><Th>Descuento hasta</Th></tr>
              </thead>
              <tbody>
                {referrals.map((r) => (
                  <tr key={r.user_id} className="border-b border-border/50 last:border-0">
                    <Td>
                      <div>{r.name || '—'}</div>
                      <div className="text-xs text-muted-foreground">{r.email}</div>
                    </Td>
                    <Td muted>{formatDate(r.signed_up_at)}</Td>
                    <Td>{TIER_LABELS[r.tier] || '—'}</Td>
                    <Td>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        r.is_active ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'
                      }`}>
                        {STATUS_LABELS[r.subscription_status] || r.subscription_status}
                      </span>
                    </Td>
                    <Td muted>{r.discount_pct}% · {formatDate(r.discount_until)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {tab === 'monthly' && (
        <Section title="Resumen por mes de pago" empty={monthly.length === 0 && 'Todavía no hay pagos registrados.'}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr><Th>Mes</Th><Th right>Pagos</Th><Th right>Facturado (sin impuestos)</Th><Th right>Reembolsado</Th><Th right>Comisión</Th></tr>
              </thead>
              <tbody>
                {monthly.map((m) => (
                  <tr key={m.month} className="border-b border-border/50 last:border-0">
                    <Td>{formatMonth(m.month)}</Td>
                    <Td right>{m.payments}</Td>
                    <Td right>{formatMoney(m.base_cents)}</Td>
                    <Td right muted>{formatMoney(m.refunded_cents)}</Td>
                    <Td right>{formatMoney(m.commission_cents)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {tab === 'payments' && (
        <Section title="Pagos de tus referidos" empty={commissions.length === 0 && 'Todavía no hay pagos registrados.'}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr><Th>Fecha</Th><Th>Usuario</Th><Th>Periodo</Th><Th right>Pagado</Th><Th right>Base</Th><Th right>Comisión</Th><Th>Estado</Th></tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id} className="border-b border-border/50 last:border-0">
                    <Td muted>{formatDate(c.paid_at)}</Td>
                    <Td>{c.referred_name || c.referred_email}</Td>
                    <Td muted>{formatDate(c.period_start)} – {formatDate(c.period_end)}</Td>
                    <Td right>{formatMoney(c.amount_paid_cents, c.currency)}</Td>
                    <Td right>{formatMoney(c.base_cents, c.currency)}</Td>
                    <Td right>{formatMoney(c.commission_cents, c.currency)}</Td>
                    <Td>
                      {c.status === 'reversed' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-400">Reembolsado</span>
                      ) : c.refunded_cents > 0 ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">Reembolso parcial</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400">Registrado</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}
